  import React, { useState, useRef, useEffect, useMemo } from "react";
  import { DataTable } from "primereact/datatable";
  import { Column } from "primereact/column";
  import { Button } from "primereact/button";
  import { Dialog } from "primereact/dialog";
  import { InputText } from "primereact/inputtext";
  import { FilterMatchMode } from "primereact/api";
  import { utils, writeFile } from "xlsx";
  import { Ripple } from "primereact/ripple";
  import { Menu } from "primereact/menu";
  import { OverlayPanel } from "primereact/overlaypanel";
  import { MultiSelect } from "primereact/multiselect";

  const ActionCell = ({ rowData, actionItems }) => {
    const op = useRef(null);

    return (
      <>
        <Button
          icon="pi pi-ellipsis-h"
          className="p-button-text p-button-rounded"
          onClick={(e) => op.current.toggle(e)}
          aria-label="Actions"
        />
        <OverlayPanel ref={op} className="p-0 shadow-lg border-0">
          <Menu
            model={actionItems.map(item => ({
              label: item.label,
              icon: item.icon,
              command: () => {
                op.current.hide();
                item.command(rowData);
              }
            }))}
            className="border-0 w-48"
          />
        </OverlayPanel>
      </>
    );
  };

  export default function ReusableTable({
    title,
    data,
    setData,
    columnFields = [],
    headerTitles = [],
    rows = 10,
    handleUpdate = () => { },
    handleDelete = () => { },
    setAddNewDialogVisible,
    onImport,
    rowEdit = false,
    rowdelete = false,
    setBulkUploadVisible,
    actionMenu = false,
    actionItems = [],
    loading = false,
    totalRecords = 0,
    onPageChange = () => { },
    first = 0,
    hideHeader = false,
    hideColumnSelector = false,
    colorScheme = {
      headerBgColor: '#1e3a8a',
      headerTextColor: '#ffffff',
      tableBgColor: '#ffffff',
      tableTextColor: '#334155',
      buttonBgColor: '#3b82f6',
      buttonTextColor: '#ffffff',
      deleteButtonColor: '#ef4444',
      addButtonColor: '#10b981',
      uploadButtonColor: '#f59e0b',
      exportButtonColor: '#6366f1'
    },
    searchValue,
    onSearchChange,
    hideSearchAndExport = false,
  }) {
    const safeData = Array.isArray(data) ? data : [];
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [columnOptions, setColumnOptions] = useState([]);

    // Memoized columns based on data structure
    const columns = useMemo(() => {
      return safeData.length > 0 ? Object.keys(safeData[0]) : [];
    }, [safeData]);

    // Memoized generated columns structure
    const generatedColumns = useMemo(() => {
      if (columnFields.length > 0) return columnFields;
      return columns.map((col, i) => ({
        field: col,
        header: headerTitles[i] || col.charAt(0).toUpperCase() + col.slice(1)
      }));
    }, [columnFields, columns, headerTitles]);

    // Update column options and manage selected columns
    useEffect(() => {
      const newColumnOptions = generatedColumns.map(col => ({
        label: col.header,
        value: col.field
      }));
      
      setColumnOptions(newColumnOptions);
      
      // Preserve existing selections while adding new columns
      setSelectedColumns(prev => {
        const currentFields = newColumnOptions.map(opt => opt.value);
        return [
          ...prev.filter(field => currentFields.includes(field)),
          ...currentFields.filter(field => !prev.includes(field))
        ];
      });
    }, [generatedColumns]);

    const dt = useRef(null);
    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [filters, setFilters] = useState({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    const [deleteRowModal, setDeleteRowModal] = useState({
      data: null,
      show: false,
    });

    const tableStyle = {
      backgroundColor: colorScheme.tableBgColor,
      color: colorScheme.tableTextColor,
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
    };

    const headerStyle = {
      backgroundColor: colorScheme.headerBgColor,
      color: colorScheme.headerTextColor,
      fontWeight: '600',
    };

    const rowClassName = (data) => {
      return {
        'hover:bg-blue-50 transition-colors': true,
        'p-0': true // Remove padding from row
      };
    };

    // Add custom cell style
    const cellStyle = {
      padding: '0.25rem 0.5rem' // Compact padding for cells
    };

    const onRowEditComplete = (e) => {
      let _tableRows = [...data];
      let { newData, index } = e;

      _tableRows[index] = newData;
      handleUpdate();
      setData(_tableRows);
    };

    const onRowDelete = (rowData) => {
      setData(data.filter((row) => row.id !== rowData.id));
      handleDelete(rowData);
      setDeleteRowModal({ data: null, show: false });
    };

    const textEditor = (options) => {
      return (
        <InputText
          type="text"
          value={options.value}
          onChange={(e) => options.editorCallback(e.target.value)}
          className="w-full p-2 border border-blue-200 rounded"
        />
      );
    };

    const allowEdit = (rowData) => {
      return rowData.name !== "Some row you dont want to edit";
    };

    const shortenTitle = (title) => {
      const sanitizedTitle = title.replace(/[\\\/:*?"<>|[\]]/g, "");
      if (sanitizedTitle.length > 31) {
        return sanitizedTitle.slice(0, 31);
      }
      return sanitizedTitle;
    };

    const exportCSV = () => {
      const dataToExport = selectedRows.length > 0 ? selectedRows : safeData;
      const colsToExport = generatedColumns.filter(col => 
        selectedColumns.includes(col.field) && col.field !== 'action'
      );
      
      const csvContent = [
        colsToExport.map(c => c.header).join(','),
        ...dataToExport.map(row => 
          colsToExport.map(c => {
            // Handle body function if exists
            if (typeof c.body === 'function') {
              const fakeColumn = {
                props: {
                  field: c.field,
                  body: c.body
                }
              };
              return `"${c.body(row, fakeColumn) || ''}"`;
            }
            return `"${row[c.field] || ''}"`;
          }).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${shortenTitle(title)}.csv`;
      link.click();
    };

    const exportToXLSX = () => {
      const dataToExport = selectedRows.length > 0 ? selectedRows : safeData;
      const colsToExport = generatedColumns.filter(col => 
        selectedColumns.includes(col.field) && col.field !== 'action'
      );
      
      const wsData = [
        colsToExport.map(c => c.header),
        ...dataToExport.map(row => 
          colsToExport.map(c => {
            // Handle body function if exists
            if (typeof c.body === 'function') {
              const fakeColumn = {
                props: {
                  field: c.field,
                  body: c.body
                }
              };
              return c.body(row, fakeColumn) || '';
            }
            return row[c.field] || '';
          })
        )
      ];
      
      const ws = utils.aoa_to_sheet(wsData);
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, shortenTitle(title));
      writeFile(wb, `${shortenTitle(title)}.xlsx`);
    };

    const onGlobalFilterChange = (e) => {
      const value = e.target.value;
      let _filters = { ...filters };
      _filters["global"].value = value;
      setFilters(_filters);
      setGlobalFilterValue(value);
    };

    const header = hideHeader ? null : (
      <div className="flex flex-col sm:flex-row justify-between items-center w-full gap-4 py-3">
        <h2 className="text-xl font-bold text-blue-700 w-full sm:w-auto order-1">{title}</h2>

        {!hideSearchAndExport && (
          <>
          {typeof onSearchChange !== 'undefined' && (
            <div className="relative w-full sm:w-2/5 order-3 sm:order-2">
              <span className="p-input-icon-left w-full relative">
                <InputText
                  value={typeof searchValue !== 'undefined' ? searchValue : globalFilterValue}
                  onChange={e => {
                    if (onSearchChange) {
                      onSearchChange(e.target.value);
                    } else {
                      onGlobalFilterChange(e);
                    }
                  }}
                  placeholder="Search..."
                  className="w-full p-3 rounded-lg border-2 border-indigo-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 shadow-sm hover:shadow-md transition-all bg-white text-gray-800 placeholder-indigo-300"
                />
              </span>
            </div>
            )}
            <div className="flex items-center justify-end gap-2 w-full sm:w-auto order-2 sm:order-3">
              
              {!hideColumnSelector && (
                <div>
                  <span className="text-sm font-bold text-gray-500 mb-1">Export Columns       </span>
                <MultiSelect
                  value={selectedColumns}
                  options={generatedColumns.map(col => ({
                    label: col.header,
                    value: col.field
                  }))}
                  onChange={(e) => setSelectedColumns(e.value)}
                  placeholder="Select Columns"
                  className="w-48 border border-gray-300 rounded-lg"
                  display="chip"
                  maxSelectedLabels={3}
                />
                </div>
              )}
              <Button
                type="button"
                icon="pi pi-file-excel"
                className="rounded-full p-2 w-10 h-10 bg-emerald-600 text-white border-2 border-emerald-400 shadow-md hover:shadow-lg hover:bg-emerald-700 transform hover:scale-105 transition-all duration-300"
                onClick={exportCSV}
                tooltip="Export CSV"
                tooltipOptions={{ position: "bottom" }}
              />
              <Button
                type="button"
                icon="pi pi-file"
                className="rounded-full p-2 w-10 h-10 bg-blue-600 text-white border-2 border-blue-400 shadow-md hover:shadow-lg hover:bg-blue-700 transform hover:scale-105 transition-all duration-300"
                onClick={exportToXLSX}
                tooltip="Export XLSX"
                tooltipOptions={{ position: "bottom" }}
              />
              {setAddNewDialogVisible && (
                <Button
                  icon="pi pi-plus"
                  className="rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold border-2 border-indigo-400 shadow-md hover:shadow-xl transition-all duration-300 flex items-center justify-center px-4 py-2"
                  onClick={() => setAddNewDialogVisible(true)}
                >
                  <span className="whitespace-nowrap hidden sm:inline ms-2">Add New</span>
                </Button>
              )}
              {onImport && (
                <Button
                  icon="pi pi-download"
                  className="rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white font-semibold border-2 border-yellow-400 shadow-md hover:shadow-xl transition-all duration-300 flex items-center justify-center px-4 py-2"
                  onClick={onImport}
                >
                  <span className="whitespace-nowrap hidden sm:inline ms-2">Import</span>
                </Button>
              )}
              {setBulkUploadVisible && (
                <Button
                  icon="pi pi-upload"
                  className="rounded-lg shadow hover:shadow-lg transition-all"
                  style={{ backgroundColor: colorScheme.uploadButtonColor }}
                  onClick={() => setBulkUploadVisible(true)}
                >
                  <span className="whitespace-nowrap hidden sm:inline ms-2">Upload CSV</span>
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    );

    const dialogFooter = (
      <div className="flex justify-end gap-2 pt-4">
        <Button
          label="Cancel"
          icon="pi pi-times"
          className="px-4 py-2 rounded-lg"
          onClick={() => setDeleteRowModal({ data: null, show: false })}
          outlined
        />
        <Button
          label="Delete"
          icon="pi pi-trash"
          className="px-4 py-2 rounded-lg"
          style={{ backgroundColor: colorScheme.deleteButtonColor }}
          onClick={() => onRowDelete(deleteRowModal.data)}
        />
      </div>
    );

    return (
      <div className={`shadow-lg rounded-xl ${!hideHeader && 'p-4'} bg-white ${!hideHeader && 'my-4'}`}>
        {header}
        <div style={{ height: 'auto' }}>
          <DataTable
            ref={dt}
            value={safeData}
            selection={selectedRows}
            onSelectionChange={(e) => setSelectedRows(e.value)}
            selectionMode="multiple"
            loading={loading}
            paginator
            rows={rows}
            totalRecords={totalRecords}
            lazy
            first={first}
            onPage={onPageChange}
            className={`custom-datatable ${!hideHeader && 'mt-3'}`}
            stripedRows
            removableSort
            showGridlines
            style={tableStyle}
            rowClassName={rowClassName}
            reorderableRows
            resizableColumns="fit"
            globalFilter={globalFilterValue}
            globalFilterFields={columns}
            exportFilename={title}
            editMode="row"
            onRowEditComplete={onRowEditComplete}
            emptyMessage="No records found"
          >
            <Column 
              selectionMode="multiple" 
              headerStyle={headerStyle}
              style={{ width: '3em' }}
              bodyStyle={cellStyle}
            />

            {generatedColumns.map((col, i) => {
              if (col.field === "action") return null;
              return (
                <Column
                  key={i}
                  field={col.field}
                  header={col.header}
                  body={col.body}
                  sortable={col.notSortable ? !col.notSortable : true}
                  filter={col.noFilter ? !col.noFilter : true}
                  editor={(options) => textEditor(options)}
                  headerStyle={headerStyle}
                  bodyStyle={cellStyle}
                  className="text-center"
                />
              );
            })}

            {actionMenu && (
              <Column
                header="Actions"
                headerStyle={headerStyle}
                bodyStyle={cellStyle}
                bodyClassName="text-center"
                style={{ width: '100px' }}
                body={(rowData) => <ActionCell rowData={rowData} actionItems={actionItems} />}
              />
            )}

            {rowEdit && (
              <Column
                rowEditor={allowEdit}
                headerStyle={headerStyle}
                bodyStyle={cellStyle}
                bodyClassName="text-center"
                header="Update"
                style={{ width: '100px' }}
              />
            )}
            {rowdelete && (
              <Column
                header="Delete"
                headerStyle={headerStyle}
                bodyStyle={cellStyle}
                bodyClassName="text-center"
                style={{ width: '100px' }}
                body={(rowData) => {
                  return (
                    <Button
                      icon="pi pi-trash"
                      className="rounded-full p-2 text-red-400 flex justify-center items-center w-8 h-8 transition-transform hover:scale-110"
                      style={{ backgroundColor: colorScheme.deleteButtonColor }}
                      onClick={() =>
                        setDeleteRowModal({ data: rowData, show: true })
                      }
                    >
                      <Ripple />
                    </Button>
                  );
                }}
              />
            )}
          </DataTable>
        </div>

        <Dialog
          header={`Delete ${title}`}
          visible={deleteRowModal.show}
          onHide={() => setDeleteRowModal({ data: null, show: false })}
          footer={dialogFooter}
          className="delete-dialog"
          style={{ width: '400px' }}
          breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        >
          <div className="confirmation-content py-4">
            <i className="pi pi-exclamation-triangle text-4xl text-red-500 mr-4" />
            <span>
              Are you sure you want to delete this record? This action cannot be
              undone.
            </span>
          </div>
        </Dialog>
      </div>
    );
  }